import React from "react";
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  FormGroup,
  FormInput,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "shards-react";
import { toast } from 'react-toastify';
import PageTitle from "../../../components/common/PageTitle";
import "./SnsSetting.scss";

@inject(({ teamUser }) => ({isLogin: teamUser.isLogin, me: teamUser.me}))
@inject(({ common }) => ({ getSnsConnections: common.getSnsConnections, disconnectSns: common.disconnectSns, finalizeFacebookPage: common.finalizeFacebookPage }))

@observer
class SnsSetting extends React.Component {
  //basic
  @observable store = {};
  @observable teamUser = {};
  @observable permissions = {}
  @observable isLogin = false;
  @observable isLoading = true;

  // 연동 실패 사유 (안내 박스에 노출)
  @observable connectionError = '';

  // Instagram 피드 동기화 기능 소개 모달
  @observable showFeatureModal = false;

  @action toggleFeatureModal = () => {
    this.showFeatureModal = !this.showFeatureModal;
  };

  // Facebook 페이지 선택 모달
  @observable showPageSelectModal = false;
  @observable facebookPagesToSelect = [];
  @observable selectedFacebookPageId = '';
  @observable isFinalizingPage = false;

  @action openPageSelectModal = (pages) => {
    this.facebookPagesToSelect = Array.isArray(pages) ? pages : [];
    this.selectedFacebookPageId = this.facebookPagesToSelect.length > 0 ? this.facebookPagesToSelect[0].id : '';
    this.showPageSelectModal = true;
  };

  @action closePageSelectModal = () => {
    this.showPageSelectModal = false;
    this.facebookPagesToSelect = [];
    this.selectedFacebookPageId = '';
  };

  @action confirmFacebookPageSelection = () => {
    if (!this.selectedFacebookPageId) {
      toast.error('연동할 Facebook 페이지를 선택해 주세요.');
      return;
    }
    if (this.isFinalizingPage) return;
    this.isFinalizingPage = true;

    const params = {
      store_id: this.store._id,
      shopNo: this.teamUser.selShopNo,
      pageId: this.selectedFacebookPageId
    };

    this.props.finalizeFacebookPage(params, (res, errors) => {
      this.isFinalizingPage = false;
      if (errors && errors.length > 0) {
        toast.error(errors[0].message || 'Facebook 페이지 선택 처리 중 오류가 발생했습니다.');
        return;
      }
      if (res && res.pageName) {
        toast.success(`Facebook 페이지 "${res.pageName}" 와 연동되었습니다.`);
      } else {
        toast.success('Facebook 페이지 연동이 완료되었습니다.');
      }
      this.closePageSelectModal();
      this.loadSnsConnections();
    });
  };

  //SNS connections
  @observable snsData = {
    instagram: {
      connected: false,
      username: "",
      userId: "",
      connectedAt: null,
      feedSettings: {
        defaultContentForImageOnly: "Instagram 에서 작성된 리뷰입니다"
      }
    },
    facebook: {
      connected: false,
      pageName: "",
      pageId: "",
      connectedAt: null,
      feedSettings: {
        defaultContentForImageOnly: "Facebook 에서 작성된 리뷰입니다"
      }
    },
    naver: {
      connected: false,
      blogId: "",
      blogName: "",
      connectedAt: null,
      feedSettings: {
        defaultContentForImageOnly: "네이버 블로그에서 작성된 리뷰입니다"
      }
    },
    youtube: {
      connected: false,
      channelName: "",
      channelId: "",
      connectedAt: null,
      feedSettings: {
        defaultContentForImageOnly: "YouTube 에서 작성된 리뷰입니다"
      }
    }
  };

  // OAuth 설정
  @observable oauthConfig = {
    instagram: {
      // Instagram OAuth 모드: 'instagram' 또는 'facebook'
      mode: process.env.REACT_APP_INSTAGRAM_OAUTH_MODE || 'facebook',
      clientId: process.env.REACT_APP_INSTAGRAM_CLIENT_ID || '',
      facebookAppId: process.env.REACT_APP_FACEBOOK_APP_ID || '',
      // Instagram 직접 OAuth
      instagramAuthUrl: 'https://www.instagram.com/oauth/authorize',
      // 온리플은 인스타 미디어(사진/영상) 가져오기만 사용한다. 아래는 미사용 권한이라 제거:
      //   manage_messages(DM) / manage_comments(댓글 조회·관리) / content_publish(발행) / manage_insights(인사이트)
      instagramScope: 'instagram_business_basic',
      // Facebook 경유 OAuth
      facebookAuthUrl: 'https://www.facebook.com/v24.0/dialog/oauth',
      // 미디어 가져오기 + FB페이지 연결(IG Graph) 최소셋만 유지. 제거: instagram_manage_comments(댓글),
      //   instagram_content_publish(발행), instagram_manage_insights(인사이트), pages_manage_posts(페이지 글 관리)
      facebookScope: 'instagram_basic,pages_read_engagement,pages_show_list,business_management',
      // 디버깅: 환경 변수 확인
      _debug: process.env.REACT_APP_INSTAGRAM_CLIENT_ID ? '✓ Instagram Client ID 설정됨' : '✗ Instagram Client ID 없음'
    },
    facebook: {
      clientId: process.env.REACT_APP_FACEBOOK_APP_ID || '',
      scope: 'pages_manage_posts,pages_read_engagement,instagram_basic,instagram_content_publish',
      authUrl: 'https://www.facebook.com/v24.0/dialog/oauth'
    },
    naver: {
      clientId: process.env.REACT_APP_NAVER_CLIENT_ID || '',
      authUrl: 'https://nid.naver.com/oauth2.0/authorize'
    },
    youtube: {
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
      scope: 'https://www.googleapis.com/auth/youtube.readonly https://www.googleapis.com/auth/youtube.upload',
      authUrl: 'https://accounts.google.com/o/oauth2/v2/auth'
    }
  };

  componentDidMount() {
    this.init();
    // OAuth 콜백 데이터 확인 및 처리
    this.checkOAuthCallback();
  }

  @action
  init = async () => {
    if(this.props.isLogin) {
      //basic
      this.isLogin = this.props.isLogin;
      this.teamUser = this.props.me;
      this.store = this.props.me.store ? this.props.me.store : {};

      // SNS 연동 정보 로드
      await this.loadSnsConnections();

      // 피드 설정 로드
      this.loadFeedSettings();
    }
  };

  @action
  checkOAuthCallback = () => {
    try {
      const callbackDataStr = localStorage.getItem('oauth_callback_data');
      if (!callbackDataStr) return;

      const callbackData = JSON.parse(callbackDataStr);
      console.log('OAuth 콜백 데이터 발견:', callbackData);

      // 데이터 처리
      if (callbackData.type === 'oauth_success') {
        const username = callbackData.username || '';
        const successMsg = username
          ? `${this.getPlatformName(callbackData.platform)} 연동이 완료되었습니다! (${username})`
          : `${this.getPlatformName(callbackData.platform)} 연동이 완료되었습니다!`;

        toast.success(successMsg);
        this.connectionError = '';
        console.log('✅ SNS 연동 성공:', callbackData);

        // SNS 연동 정보 새로고침
        this.loadSnsConnections();
      } else if (callbackData.type === 'oauth_page_selection') {
        // Facebook 페이지가 여러 개 — 선택 모달 노출
        console.log('🔀 Facebook 페이지 선택 필요:', callbackData.pages);
        this.connectionError = '';
        this.openPageSelectModal(callbackData.pages || []);
      } else if (callbackData.type === 'oauth_error') {
        this.connectionError = callbackData.message || 'SNS 연동 중 오류가 발생했습니다.';
        console.error('❌ SNS 연동 실패:', callbackData.message);
      }

      // localStorage 정리
      localStorage.removeItem('oauth_callback_data');

      // sessionStorage 정리
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_platform');
      sessionStorage.removeItem('oauth_return_url');
    } catch (error) {
      console.error('OAuth 콜백 데이터 처리 오류:', error);
    }
  };

  @action
  loadSnsConnections = async () => {
    this.isLoading = true;

    try {
      const params = {
        store_id: this.store._id,
        shopNo: this.teamUser.selShopNo,
      };

      // GraphQL 쿼리로 SNS 연동 정보 가져오기
      await this.props.getSnsConnections(params, (res) => {
        console.log(res)
        if(res && res.data) {
          this.snsData = res.data;

          // feedSettings가 없는 경우 초기화
          Object.keys(this.snsData).forEach(platform => {
            if (this.snsData[platform] && !this.snsData[platform].feedSettings) {
              this.snsData[platform].feedSettings = {
                defaultContentForImageOnly: this.getDefaultContentText(platform)
              };
            }
          });

        }
      });
    } catch (error) {
      console.error('SNS 연동 정보 로드 실패:', error);
    } finally {
      this.isLoading = false;
    }
  };

  @action
  connectInstagram = () => {
    const config = this.oauthConfig.instagram;
    const oauthMode = config.mode; // 'instagram' 또는 'facebook'

    console.log('=== Instagram OAuth 모드 ===');
    console.log('OAuth Mode:', oauthMode);
    console.log('===========================');

    if (oauthMode === 'facebook') {
      // Facebook 경유 Instagram OAuth
      this.connectInstagramViaFacebook();
    } else {
      // Instagram 직접 OAuth
      this.connectInstagramDirect();
    }
  };

  @action
  connectInstagramDirect = () => {
    // Instagram OAuth 직접 연동 (Facebook 경유 없이)
    const config = this.oauthConfig.instagram;
    const instagramClientId = config.clientId;

    // 디버깅: 환경 변수 전체 확인
    console.log('=== Instagram 직접 OAuth 디버깅 ===');
    console.log('Instagram Client ID:', instagramClientId);
    console.log('typeof instagramClientId:', typeof instagramClientId);
    console.log('instagramClientId.length:', instagramClientId.length);
    console.log('====================================');

    if (!instagramClientId) {
      toast.error('Instagram Client ID가 설정되지 않았습니다. .env 파일을 확인해주세요.');
      return;
    }

    // Client ID 검증
    if (instagramClientId.trim().length < 10) {
      toast.error('Instagram Client ID가 올바르지 않습니다. .env 파일을 확인해주세요.');
      console.error('Invalid Instagram Client ID:', instagramClientId);
      return;
    }

    // redirect URI https://ocadmin.onreview.co.kr/instagram_callback
    const redirectUri = encodeURIComponent(`${window.location.origin}/instagram_callback`);
    const scope = encodeURIComponent(config.instagramScope);

    // Instagram OAuth URL (직접 연동)
    const authUrl = `${config.instagramAuthUrl}?force_reauth=true&client_id=${instagramClientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}`;

    // 디버깅: URL 확인
    console.log('=== Instagram 직접 OAuth Debug ===');
    console.log('Instagram Client ID:', instagramClientId);
    console.log('Current Origin:', window.location.origin);
    console.log('Redirect URI:', `${window.location.origin}/instagram_callback`);
    console.log('Scope:', config.instagramScope);
    console.log('Auth URL:', authUrl);
    console.log('====================================');

    // state를 세션에 저장
    sessionStorage.setItem('oauth_platform', 'instagram');
    sessionStorage.setItem('oauth_return_url', window.location.href);

    // OAuth URL로 이동
    window.location.href = authUrl;
  };

  @action
  connectInstagramViaFacebook = () => {
    // Facebook 경유 Instagram OAuth
    const config = this.oauthConfig.instagram;
    const facebookAppId = config.facebookAppId;

    // 디버깅: 환경 변수 확인
    console.log('=== Facebook 경유 Instagram OAuth 디버깅 ===');
    console.log('Facebook App ID:', facebookAppId);
    console.log('typeof facebookAppId:', typeof facebookAppId);
    console.log('facebookAppId.length:', facebookAppId.length);
    console.log('==========================================');

    if (!facebookAppId) {
      toast.error('Facebook App ID가 설정되지 않았습니다. .env 파일을 확인해주세요.');
      return;
    }

    // Client ID 검증
    if (facebookAppId.trim().length < 10) {
      toast.error('Facebook App ID가 올바르지 않습니다. .env 파일을 확인해주세요.');
      console.error('Invalid Facebook App ID:', facebookAppId);
      return;
    }

    // redirect URI
    const redirectUri = encodeURIComponent(`${window.location.origin}/instagram_callback`);
    const scope = encodeURIComponent(config.facebookScope);

    // Facebook OAuth URL (Instagram 권한 포함)
    // auth_type=rerequest — 이전 동의에서 거부/미선택된 권한·페이지를 다시 묻도록 강제
    const authUrl = `${config.facebookAuthUrl}?client_id=${facebookAppId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=code&auth_type=rerequest`;

    // 디버깅: URL 확인
    console.log('=== Facebook 경유 Instagram OAuth Debug ===');
    console.log('Facebook App ID:', facebookAppId);
    console.log('Current Origin:', window.location.origin);
    console.log('Redirect URI:', `${window.location.origin}/instagram_callback`);
    console.log('Scope:', config.facebookScope);
    console.log('Auth URL:', authUrl);
    console.log('==========================================');

    // state를 세션에 저장
    sessionStorage.setItem('oauth_platform', 'instagram');
    sessionStorage.setItem('oauth_mode', 'facebook');
    sessionStorage.setItem('oauth_return_url', window.location.href);

    // OAuth URL로 이동
    window.location.href = authUrl;
  };

  @action
  connectFacebook = () => {
    const config = this.oauthConfig.facebook;

    if (!config.clientId) {
      toast.error('Facebook App ID가 설정되지 않았습니다. 환경 변수를 확인해주세요.');
      return;
    }

    const redirectUri = encodeURIComponent(`${window.location.origin}/sns/facebook/callback`);
    const state = this.generateState();

    const authUrl = `${config.authUrl}?client_id=${config.clientId}&redirect_uri=${redirectUri}&scope=${config.scope}&response_type=code&state=${state}`;

    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_platform', 'facebook');

    // 팝업으로 OAuth 처리
    this.openOAuthPopup(authUrl, 'facebook');
  };

  @action
  connectNaver = () => {
    const config = this.oauthConfig.naver;

    if (!config.clientId) {
      toast.error('네이버 Client ID가 설정되지 않았습니다. 환경 변수를 확인해주세요.');
      return;
    }

    const redirectUri = encodeURIComponent(`${window.location.origin}/sns/naver/callback`);
    const state = this.generateState();

    const authUrl = `${config.authUrl}?response_type=code&client_id=${config.clientId}&redirect_uri=${redirectUri}&state=${state}`;

    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_platform', 'naver');

    // 팝업으로 OAuth 처리
    this.openOAuthPopup(authUrl, 'naver');
  };

  @action
  connectYoutube = () => {
    const config = this.oauthConfig.youtube;

    if (!config.clientId) {
      toast.error('Google Client ID가 설정되지 않았습니다. 환경 변수를 확인해주세요.');
      return;
    }

    const redirectUri = encodeURIComponent(`${window.location.origin}/sns/youtube/callback`);
    const state = this.generateState();

    const authUrl = `${config.authUrl}?client_id=${config.clientId}&redirect_uri=${redirectUri}&scope=${encodeURIComponent(config.scope)}&response_type=code&access_type=offline&state=${state}&prompt=consent`;

    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oauth_platform', 'youtube');

    // 팝업으로 OAuth 처리
    this.openOAuthPopup(authUrl, 'youtube');
  };

  generateState = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  /**
   * OAuth 팝업 열기 및 메시지 수신 처리
   */
  openOAuthPopup = (authUrl, platform) => {
    // 팝업 창 크기 및 위치 계산
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    // 고유 ID 생성 (팝업 식별용)
    const popupId = `popup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // 팝업 열기
    const popup = window.open(
      authUrl,
      `${platform}_oauth`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      toast.error('팝업이 차단되었습니다. 브라우저 팝업 차단을 해제해주세요.');
      return;
    }

    // 팝업에서 사용할 수 있도록 sessionStorage에 부모 창 정보 저장
    // sessionStorage는 같은 도메인의 팝업에서 공유됨
    try {
      sessionStorage.setItem('oauth_popup_id', popupId);
      sessionStorage.setItem('oauth_popup_platform', platform);
      sessionStorage.setItem('oauth_popup_opened', 'true');
      console.log('팝업 정보 저장 (sessionStorage):', popupId, 'Platform:', platform);
    } catch (e) {
      console.error('sessionStorage 저장 실패:', e);
    }

    // 팝업에서 메시지 수신 대기
    const messageHandler = (event) => {
      // 보안: origin 확인
      if (event.origin !== window.location.origin) {
        return;
      }

      // OAuth 성공 메시지 처리
      if (event.data.type === 'oauth_success' && event.data.platform === platform) {
        toast.success(`${this.getPlatformName(platform)} 연동이 완료되었습니다!`);

        // SNS 연동 정보 새로고침
        this.loadSnsConnections();

        // 이벤트 리스너 제거
        window.removeEventListener('message', messageHandler);

        // 팝업 닫기
        if (popup && !popup.closed) {
          popup.close();
        }
      }

      // OAuth 실패 메시지 처리
      if (event.data.type === 'oauth_error' && event.data.platform === platform) {
        this.connectionError = event.data.message || `${this.getPlatformName(platform)} 연동 중 오류가 발생했습니다.`;

        // 이벤트 리스너 제거
        window.removeEventListener('message', messageHandler);

        // 팝업 닫기
        if (popup && !popup.closed) {
          popup.close();
        }
      }
    };

    // 메시지 이벤트 리스너 등록
    window.addEventListener('message', messageHandler);

    // localStorage polling (window.opener가 작동하지 않을 때 대체 수단)
    let lastCheckedTimestamp = Date.now();
    const pollingInterval = setInterval(() => {
      try {
        const callbackDataStr = localStorage.getItem('oauth_callback_data');
        if (!callbackDataStr) return;

        const callbackData = JSON.parse(callbackDataStr);
        console.log('localStorage polling - 데이터 발견:', callbackData);

        // 타임스탬프 체크 (새로운 데이터인지 확인)
        if (callbackData.timestamp && callbackData.timestamp > lastCheckedTimestamp) {
          console.log('새로운 콜백 데이터 처리:', callbackData);

          if (callbackData.platform === platform) {
            if (callbackData.type === 'oauth_success') {
              // 성공 메시지 표시 (사용자 정보 포함)
              const username = callbackData.username || '';
              const successMsg = username
                ? `${this.getPlatformName(platform)} 연동이 완료되었습니다! (${username})`
                : `${this.getPlatformName(platform)} 연동이 완료되었습니다!`;

              toast.success(successMsg);
              this.connectionError = '';
              console.log('✅ SNS 연동 성공:', {
                platform: platform,
                username: username,
                data: callbackData.data
              });

              // SNS 연동 정보 새로고침
              this.loadSnsConnections();
            } else if (callbackData.type === 'oauth_error') {
              this.connectionError = callbackData.message || `${this.getPlatformName(platform)} 연동 중 오류가 발생했습니다.`;
              console.error('❌ SNS 연동 실패:', callbackData.message);
            }

            // 리스너 제거
            clearInterval(pollingInterval);
            window.removeEventListener('message', messageHandler);

            // localStorage 정리
            localStorage.removeItem('oauth_callback_data');

            // sessionStorage 정리
            sessionStorage.removeItem('oauth_popup_id');
            sessionStorage.removeItem('oauth_popup_platform');
            sessionStorage.removeItem('oauth_popup_opened');

            // 팝업 닫기
            if (popup && !popup.closed) {
              popup.close();
            }
          }

          lastCheckedTimestamp = callbackData.timestamp;
        }
      } catch (error) {
        console.error('localStorage polling 오류:', error);
      }
    }, 500); // 0.5초마다 체크

    // 팝업이 닫혔는지 주기적으로 확인
    const checkPopupClosed = setInterval(() => {
      if (popup.closed) {
        console.log('팝업이 닫혔습니다. 리스너 정리');
        clearInterval(checkPopupClosed);
        clearInterval(pollingInterval);
        window.removeEventListener('message', messageHandler);

        // sessionStorage 정리
        sessionStorage.removeItem('oauth_popup_id');
        sessionStorage.removeItem('oauth_popup_platform');
        sessionStorage.removeItem('oauth_popup_opened');
      }
    }, 1000);
  };

  @action
  disconnectSns = async (platform) => {
    if(!window.confirm(`${this.getPlatformName(platform)} 연동을 해제하시겠습니까?`)) {
      return;
    }

    try {
      const params = {
        store_id: this.store._id,
        shopNo: this.teamUser.selShopNo
      };

      await this.props.disconnectSns(params, (res) => {
        console.log('disconnectSns response:', res);

        if(res && res.success) {
          toast.success(res.message || `${this.getPlatformName(platform)} 연동이 해제되었습니다.`);

          // SNS 연동 정보 새로고침
          this.loadSnsConnections();
        } else {
          toast.error(res.message || '연동 해제 중 오류가 발생했습니다.');
        }
      });
    } catch (error) {
      console.error('disconnectSns error:', error);
      toast.error("연동 해제 중 오류가 발생했습니다.");
    }
  };

  getPlatformName = (platform) => {
    const names = {
      instagram: 'Instagram',
      facebook: 'Facebook',
      naver: '네이버 블로그',
      youtube: 'YouTube'
    };
    return names[platform] || platform;
  };

  getDefaultContentText = (platform) => {
    const defaultTexts = {
      instagram: "Instagram 에서 작성된 리뷰입니다",
      facebook: "Facebook 에서 작성된 리뷰입니다",
      naver: "네이버 블로그에서 작성된 리뷰입니다",
      youtube: "YouTube 에서 작성된 리뷰입니다"
    };
    return defaultTexts[platform] || "";
  };

  @action
  onChangeFeedSetting = (platform, event) => {
    const { name, value } = event.target;
    if (this.snsData[platform]) {
      // feedSettings가 없으면 초기화
      if (!this.snsData[platform].feedSettings) {
        this.snsData[platform].feedSettings = {
          defaultContentForImageOnly: this.getDefaultContentText(platform)
        };
      }
      this.snsData[platform].feedSettings[name] = value;
    }
  };

  @action
  saveFeedSettings = (platform) => {
    // TODO: API를 통해 설정 저장 (필요시 구현)
    // 현재는 localStorage에 저장
    const settings = {};
    Object.keys(this.snsData).forEach(key => {
      if (this.snsData[key].feedSettings) {
        settings[key] = this.snsData[key].feedSettings;
      }
    });
    localStorage.setItem('snsFeedSettings', JSON.stringify(settings));
    toast.success(`${this.getPlatformName(platform)} 피드 설정이 저장되었습니다.`);
  };

  @action
  loadFeedSettings = () => {
    // localStorage에서 설정 불러오기
    const saved = localStorage.getItem('snsFeedSettings');
    if (saved) {
      try {
        const settings = JSON.parse(saved);
        Object.keys(settings).forEach(platform => {
          if (this.snsData[platform] && this.snsData[platform].feedSettings) {
            this.snsData[platform].feedSettings = settings[platform];
          }
        });
      } catch (error) {
        console.error('Failed to load feed settings:', error);
      }
    }
  };

  renderSnsCard = (platform, icon, color) => {
    const data = this.snsData[platform];
    const config = this.oauthConfig[platform];
    const hasClientId = !!config.clientId;

    // 준비중인 플랫폼 (Facebook, Naver, YouTube)
    const isComingSoon = ['facebook', 'naver', 'youtube'].includes(platform);

    return (
      <Col lg="12" className="mb-4" key={platform}>
        <Card className="sns-card" style={{position: 'relative', overflow: 'hidden'}}>
          <CardHeader className="border-bottom">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className={`material-icons mr-2`} style={{color: color, fontSize: '32px'}}>{icon}</i>
                <h6 className="m-0">{this.getPlatformName(platform)}</h6>
              </div>
              {isComingSoon ? (
                <Badge theme="warning">준비중</Badge>
              ) : data.connected ? (
                <Badge theme="success">연동됨</Badge>
              ) : (
                <Badge theme="secondary">미연동</Badge>
              )}
            </div>
          </CardHeader>
          <CardBody style={{position: 'relative'}}>
            {isComingSoon ? (
              <>
                <div className="coming-soon-info">
                  <div className="alert alert-warning mb-3" style={{fontSize: '0.9rem', padding: '15px'}}>
                    <i className="material-icons" style={{fontSize: '24px', verticalAlign: 'middle'}}>schedule</i>
                    <strong className="ml-2">서비스 준비중입니다</strong>
                  </div>
                  <p className="text-muted mb-3">
                    {platform === 'facebook' && 'Facebook 페이지 연동 기능은 현재 개발 중입니다. 곧 만나보실 수 있습니다.'}
                    {platform === 'naver' && '네이버 블로그 연동 기능은 현재 개발 중입니다. 곧 만나보실 수 있습니다.'}
                    {platform === 'youtube' && 'YouTube 채널 연동 기능은 현재 개발 중입니다. 곧 만나보실 수 있습니다.'}
                  </p>
                  <Button
                    theme="secondary"
                    disabled
                  >
                    <i className="material-icons mr-1">lock</i>
                    준비중
                  </Button>
                </div>
                {/* 회색 투명 암막 */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '100%',
                  background: 'linear-gradient(to bottom, rgba(128, 128, 128, 0) 0%, rgba(128, 128, 128, 0.3) 50%, rgba(128, 128, 128, 0.5) 100%)',
                  pointerEvents: 'none',
                  zIndex: 1
                }} />
              </>
            ) : data.connected ? (
              <div className="connected-info">
                <Row>
                  <Col md="6">
                    <div className="info-item mb-3">
                      <label>계정 정보</label>
                      <div className="info-value">
                        {platform === 'instagram' && data.username}
                        {platform === 'facebook' && data.pageName}
                        {platform === 'naver' && data.blogId}
                        {platform === 'youtube' && data.channelName}
                      </div>
                    </div>
                    <div className="info-item mb-3">
                      <label>연동 상태</label>
                      <div className="info-value">
                        <Badge theme="success" className="mr-2">정상</Badge>
                        <small className="text-muted">활성화됨</small>
                      </div>
                    </div>
                    {data.connectedAt && (
                      <div className="info-item mb-3">
                        <label>연동 일시</label>
                        <div className="info-value">
                          <small className="text-muted">
                            {new Date(data.connectedAt).toLocaleString('ko-KR')}
                          </small>
                        </div>
                      </div>
                    )}
                    <div className="d-flex gap-2">
                      {platform === 'instagram' && (
                        <Button
                          theme="info"
                          size="sm"
                          onClick={() => window.location.href = '/myInstagramFeed'}
                          className="mr-2"
                        >
                          <i className="material-icons mr-1" style={{fontSize: '16px', verticalAlign: 'middle'}}>feed</i>
                          내 피드 보기
                        </Button>
                      )}
                      <Button
                        theme="danger"
                        size="sm"
                        onClick={() => this.disconnectSns(platform)}
                      >
                        연동 해제
                      </Button>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="feed-settings-box" style={{padding: '15px', background: '#f8f9fa', borderRadius: '4px'}}>
                      <h6 className="mb-3">피드 설정</h6>
                      <FormGroup>
                        <label htmlFor={`${platform}-defaultContent`} style={{fontSize: '0.9rem'}}>
                          이미지만 있고 내용이 없는 피드 기본 텍스트
                        </label>
                        <FormInput
                          id={`${platform}-defaultContent`}
                          name="defaultContentForImageOnly"
                          placeholder="예: 고객님의 소중한 리뷰 감사합니다"
                          value={data.feedSettings && data.feedSettings.defaultContentForImageOnly ? data.feedSettings.defaultContentForImageOnly : ''}
                          onChange={(e) => this.onChangeFeedSetting(platform, e)}
                          size="sm"
                        />
                        <small className="form-text text-muted">
                          SNS 피드에 이미지는 있지만 캡션/내용이 없는 경우 자동으로 입력될 텍스트
                        </small>
                      </FormGroup>
                      <Button theme="primary" size="sm" onClick={() => this.saveFeedSettings(platform)}>
                        <i className="material-icons mr-1" style={{fontSize: '14px', verticalAlign: 'middle'}}>save</i>
                        설정 저장
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            ) : (
              <div className="disconnected-info">
                <p className="text-muted mb-3">
                  {platform === 'instagram' && 'Instagram 계정을 연동하여 리뷰를 자동으로 공유하세요.'}
                  {platform === 'facebook' && 'Facebook 페이지를 연동하여 리뷰를 자동으로 게시하세요.'}
                  {platform === 'naver' && '네이버 블로그를 연동하여 리뷰를 자동으로 포스팅하세요.'}
                  {platform === 'youtube' && 'YouTube 채널을 연동하여 리뷰 영상을 자동으로 업로드하세요.'}
                </p>
                {!hasClientId && (
                  <div className="alert alert-warning mb-3" style={{fontSize: '0.85rem'}}>
                    <i className="material-icons" style={{fontSize: '18px', verticalAlign: 'middle'}}>warning</i>
                    {' '}환경 변수에 Client ID가 설정되지 않았습니다.
                  </div>
                )}
                <div className="d-flex align-items-center">
                  <Button
                    theme="primary"
                    onClick={() => {
                      if(platform === 'instagram') this.connectInstagram();
                      if(platform === 'facebook') this.connectFacebook();
                      if(platform === 'naver') this.connectNaver();
                      if(platform === 'youtube') this.connectYoutube();
                    }}
                    disabled={!hasClientId}
                  >
                    <i className="material-icons mr-1">link</i>
                    연동하기
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    );
  };

  render() {
    return (
      <Container fluid className="main-content-container px-4 sns-connection-page">
        <Row noGutters className="page-header py-4">
          <PageTitle
            title="SNS 연동 관리"
            subtitle="소셜 미디어 계정을 연동하여 리뷰를 자동으로 공유하세요."
            className="text-sm-left mb-3"
          />
        </Row>

        <Row className="mb-4">
          <Col>
            <Card className="mb-4">
              <CardBody className="p-4" style={{backgroundColor: '#f8f9fa'}}>
                <div className="d-flex align-items-start">
                  <i className="material-icons mr-3" style={{fontSize: '40px', color: '#17a2b8'}}>info</i>
                  <div style={{flex: 1}}>
                    <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap" style={{gap: '8px'}}>
                      <h6 className="m-0">SNS 연동 안내</h6>
                      <Button
                        size="sm"
                        onClick={this.toggleFeatureModal}
                        style={{
                          background: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)',
                          border: 'none',
                          color: '#fff',
                          fontWeight: '600',
                          padding: '6px 14px',
                          boxShadow: '0 2px 6px rgba(253, 29, 29, 0.3)'
                        }}
                      >
                        <i className="material-icons mr-1" style={{fontSize: '16px', verticalAlign: 'middle'}}>auto_awesome</i>
                        Instagram 피드 동기화 기능 소개
                      </Button>
                    </div>
                    <p className="mb-2" style={{fontSize: '0.9rem', color: '#dc3545', lineHeight: '1.5'}}>
                      * Instagram 비즈니스 또는 크리에이터 계정이어야 하며, Facebook 페이지와 연결되어 있어야 합니다.
                    </p>
                    {this.connectionError && (
                      <div className="mt-3 p-3"
                           style={{backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: '4px'}}>
                        <div className="d-flex align-items-start justify-content-between">
                          <div style={{fontSize: '0.9rem', color: '#842029', lineHeight: '1.6', whiteSpace: 'pre-line'}}>
                            <strong>연동 실패:</strong> {this.connectionError}
                          </div>
                          <button
                            type="button"
                            onClick={() => { this.connectionError = ''; }}
                            style={{background: 'transparent', border: 'none', color: '#842029', fontSize: '1.1rem', lineHeight: 1, cursor: 'pointer', marginLeft: '12px'}}
                            aria-label="닫기"
                          >
                            ×
                          </button>
                        </div>
                        {/* "연동된 Facebook 페이지가 없습니다." 케이스 — 페이지 선택을 다시 받기 위한 가이드 */}
                        {/연동된 Facebook 페이지가 없습니다|pages_show_list/i.test(this.connectionError) && (
                          <div className="mt-3 p-3"
                               style={{backgroundColor: '#fff3cd', border: '1px solid #ffe69c', borderRadius: '4px', color: '#664d03', fontSize: '0.85rem', lineHeight: '1.7'}}>
                            <strong>해결 방법:</strong>
                            <ol style={{paddingLeft: '20px', margin: '6px 0 0'}}>
                              <li>아래 링크에서 Facebook의 비즈니스 통합(연동된 앱) 목록을 열고, 온리플 앱을 찾아 <strong>제거</strong> 또는 <strong>"앱 권한 보기 → 페이지 다시 선택"</strong> 처리해 주세요.</li>
                              <li>그 다음 본 페이지에서 <strong>다시 [연동하기]</strong> 를 눌러 동의 화면이 다시 뜨면, <strong>대상 Facebook 페이지를 모두 체크</strong>한 뒤 진행해 주세요.</li>
                            </ol>
                            <a href="https://www.facebook.com/settings?tab=business_tools"
                               target="_blank" rel="noopener noreferrer"
                               style={{display: 'inline-block', marginTop: '8px', color: '#0d6efd', fontWeight: 600}}>
                              Facebook 비즈니스 통합 설정 열기 ↗
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {this.renderSnsCard('instagram', 'camera_alt', '#E4405F')}
          {/*{this.renderSnsCard('facebook', 'facebook', '#1877F2')}
          {this.renderSnsCard('naver', 'article', '#03C75A')}
          {this.renderSnsCard('youtube', 'play_circle', '#FF0000')}*/}
        </Row>

        {/*<Row className="mt-4">
          <Col>
            <Card>
              <CardHeader className="border-bottom">
                <h6 className="m-0">자동 공유 설정</h6>
              </CardHeader>
              <CardBody>
                <FormGroup className="mb-3">
                  <label className="d-flex align-items-center">
                    <input type="checkbox" className="mr-2" />
                    <span>포토 리뷰만 자동 공유</span>
                  </label>
                </FormGroup>
                <FormGroup className="mb-3">
                  <label className="d-flex align-items-center">
                    <input type="checkbox" className="mr-2" />
                    <span>평점 4.5 이상 리뷰만 공유</span>
                  </label>
                </FormGroup>
                <FormGroup className="mb-3">
                  <label className="d-flex align-items-center">
                    <input type="checkbox" className="mr-2" />
                    <span>관리자 승인 후 공유</span>
                  </label>
                </FormGroup>
                <Button theme="primary" size="sm">
                  설정 저장
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>*/}

        {/* Instagram 피드 동기화 기능 소개 모달 */}
        <Modal open={this.showFeatureModal} toggle={this.toggleFeatureModal} size="lg" centered>
          <ModalHeader
            toggle={this.toggleFeatureModal}
            style={{
              background: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)',
              color: '#fff',
              borderBottom: 'none'
            }}
          >
            <div className="d-flex align-items-center">
              <i className="material-icons mr-2" style={{fontSize: '28px'}}>auto_awesome</i>
              <span style={{fontWeight: '600'}}>Instagram 피드 동기화 기능</span>
            </div>
          </ModalHeader>
          <ModalBody style={{padding: '24px'}}>
            {/* 인트로 */}
            <div className="mb-4" style={{padding: '16px', background: '#FDF4FF', borderRadius: '8px', border: '1px solid #F3E8FF'}}>
              <p className="m-0" style={{fontSize: '0.95rem', lineHeight: '1.7', color: '#4A148C'}}>
                <strong>Instagram에 올린 사진/영상</strong>을 자동으로 가져와 <strong>쇼핑몰 리뷰 자산</strong>으로 활용하세요.
                포토 리뷰가 부족한 상품도 SNS 콘텐츠로 풍성하게 채울 수 있습니다.
              </p>
            </div>

            {/* 주요 혜택 */}
            <h6 className="mb-3" style={{fontWeight: '600', color: '#333'}}>
              <i className="material-icons mr-2" style={{verticalAlign: 'middle', color: '#E4405F'}}>favorite</i>
              주요 혜택
            </h6>
            <Row className="mb-4">
              <Col md="6" className="mb-3">
                <div style={{padding: '14px', background: '#fff', border: '1px solid #e9ecef', borderRadius: '8px', height: '100%'}}>
                  <div className="d-flex align-items-start">
                    <i className="material-icons mr-2" style={{fontSize: '24px', color: '#5664D2'}}>sync</i>
                    <div>
                      <div style={{fontWeight: '600', fontSize: '0.9rem', marginBottom: '4px'}}>자동 동기화</div>
                      <div style={{fontSize: '0.85rem', color: '#6c757d', lineHeight: '1.5'}}>
                        Instagram 신규 게시물이 올라오면 자동으로 가져옵니다.
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md="6" className="mb-3">
                <div style={{padding: '14px', background: '#fff', border: '1px solid #e9ecef', borderRadius: '8px', height: '100%'}}>
                  <div className="d-flex align-items-start">
                    <i className="material-icons mr-2" style={{fontSize: '24px', color: '#10B981'}}>photo_library</i>
                    <div>
                      <div style={{fontWeight: '600', fontSize: '0.9rem', marginBottom: '4px'}}>포토 리뷰 보강</div>
                      <div style={{fontSize: '0.85rem', color: '#6c757d', lineHeight: '1.5'}}>
                        포토 리뷰가 부족한 상품에도 풍부한 시각 콘텐츠를 노출합니다.
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md="6" className="mb-3">
                <div style={{padding: '14px', background: '#fff', border: '1px solid #e9ecef', borderRadius: '8px', height: '100%'}}>
                  <div className="d-flex align-items-start">
                    <i className="material-icons mr-2" style={{fontSize: '24px', color: '#F59E0B'}}>auto_fix_high</i>
                    <div>
                      <div style={{fontWeight: '600', fontSize: '0.9rem', marginBottom: '4px'}}>상품 자동 매칭</div>
                      <div style={{fontSize: '0.85rem', color: '#6c757d', lineHeight: '1.5'}}>
                        피드 내용/해시태그를 분석해서 관련 상품과 자동으로 연결합니다.
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md="6" className="mb-3">
                <div style={{padding: '14px', background: '#fff', border: '1px solid #e9ecef', borderRadius: '8px', height: '100%'}}>
                  <div className="d-flex align-items-start">
                    <i className="material-icons mr-2" style={{fontSize: '24px', color: '#E4405F'}}>trending_up</i>
                    <div>
                      <div style={{fontWeight: '600', fontSize: '0.9rem', marginBottom: '4px'}}>전환율 상승</div>
                      <div style={{fontSize: '0.85rem', color: '#6c757d', lineHeight: '1.5'}}>
                        실제 사용 사진/영상이 구매 의사 결정에 직접적인 영향을 줍니다.
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* 이용 단계 */}
            <h6 className="mb-3" style={{fontWeight: '600', color: '#333'}}>
              <i className="material-icons mr-2" style={{verticalAlign: 'middle', color: '#5664D2'}}>flag</i>
              이용 단계
            </h6>
            <div className="mb-4">
              {[
                {step: 1, title: 'Instagram 계정 연동', desc: '아래 [연동하기] 버튼으로 비즈니스/크리에이터 계정을 연결합니다.', icon: 'link'},
                {step: 2, title: '피드 자동 수집', desc: '연동 완료 후 Instagram 게시물이 자동으로 동기화됩니다.', icon: 'cloud_download'},
                {step: 3, title: '내 피드에서 관리', desc: '[내 피드 보기] 에서 가져온 피드를 확인하고 노출 여부를 관리합니다.', icon: 'tune'},
                {step: 4, title: '쇼핑몰 위젯 노출', desc: '리뷰 위젯/SNS 위젯을 통해 쇼핑몰에 자동으로 표시됩니다.', icon: 'storefront'}
              ].map(s => (
                <div key={s.step} className="d-flex align-items-start mb-3" style={{padding: '12px', background: '#F8FAFC', borderRadius: '6px'}}>
                  <div style={{
                    minWidth: '32px', height: '32px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 100%)',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: '700', fontSize: '0.85rem', marginRight: '12px'
                  }}>{s.step}</div>
                  <div style={{flex: 1}}>
                    <div style={{fontWeight: '600', fontSize: '0.9rem', marginBottom: '2px', color: '#333'}}>
                      <i className="material-icons mr-1" style={{fontSize: '16px', verticalAlign: 'middle', color: '#5664D2'}}>{s.icon}</i>
                      {s.title}
                    </div>
                    <div style={{fontSize: '0.85rem', color: '#6c757d', lineHeight: '1.5'}}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* 사전 조건 */}
            <div style={{padding: '14px', background: '#FFF7ED', borderRadius: '8px', border: '1px solid #FED7AA'}}>
              <div style={{fontWeight: '600', color: '#9A3412', marginBottom: '8px', fontSize: '0.9rem'}}>
                <i className="material-icons mr-1" style={{fontSize: '18px', verticalAlign: 'middle'}}>warning_amber</i>
                연동 전 확인사항
              </div>
              <ul style={{margin: 0, paddingLeft: '20px', fontSize: '0.85rem', color: '#7C2D12', lineHeight: '1.7'}}>
                <li>Instagram 계정이 <strong>비즈니스</strong> 또는 <strong>크리에이터</strong> 계정으로 전환되어 있어야 합니다.</li>
                <li>Instagram 계정이 <strong>Facebook 페이지</strong>와 연결되어 있어야 합니다.</li>
                <li>최초 1회 동기화는 게시물 수에 따라 시간이 소요될 수 있습니다.</li>
              </ul>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button theme="light" onClick={this.toggleFeatureModal}>
              닫기
            </Button>
            <Button
              onClick={() => { this.toggleFeatureModal(); this.connectInstagram(); }}
              disabled={!!(this.snsData && this.snsData.instagram && this.snsData.instagram.connected)}
              style={{
                background: 'linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)',
                border: 'none',
                color: '#fff',
                fontWeight: '600'
              }}
            >
              <i className="material-icons mr-1" style={{fontSize: '16px', verticalAlign: 'middle'}}>
                {this.snsData && this.snsData.instagram && this.snsData.instagram.connected ? 'check_circle' : 'link'}
              </i>
              {this.snsData && this.snsData.instagram && this.snsData.instagram.connected ? '이미 연동됨' : '지금 Instagram 연동하기'}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Facebook 페이지 선택 모달 */}
        <Modal open={this.showPageSelectModal} toggle={this.closePageSelectModal} centered>
          <ModalHeader toggle={this.closePageSelectModal}>
            <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
              <i className="material-icons" style={{fontSize: 20, color: '#1877F2'}}>facebook</i>
              연동할 Facebook Page 선택
            </span>
          </ModalHeader>
          <ModalBody style={{padding: '20px 24px'}}>
            <p style={{margin: '0 0 14px', fontSize: 13.5, color: '#475569', lineHeight: 1.6}}>
              계정에 연결된 페이지가 여러 개입니다. 온리플과 연동할 페이지 1개를 선택해 주세요.
            </p>
            {this.facebookPagesToSelect.length === 0 ? (
              <div style={{textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13}}>
                선택 가능한 Facebook 페이지가 없습니다.
              </div>
            ) : (
              <div style={{display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto'}}>
                {this.facebookPagesToSelect.map((page) => {
                  const checked = this.selectedFacebookPageId === page.id;
                  return (
                    <label
                      key={page.id}
                      htmlFor={`fb-page-${page.id}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 14px',
                        border: '1px solid ' + (checked ? '#1877F2' : '#E2E8F0'),
                        background: checked ? '#EFF6FF' : '#FFFFFF',
                        borderRadius: 10,
                        cursor: 'pointer',
                        transition: 'border-color 0.15s, background 0.15s',
                        margin: 0
                      }}
                    >
                      <input
                        id={`fb-page-${page.id}`}
                        type="radio"
                        name="facebookPage"
                        value={page.id}
                        checked={checked}
                        onChange={() => { this.selectedFacebookPageId = page.id; }}
                        style={{accentColor: '#1877F2', marginRight: 0}}
                      />
                      <div style={{flex: 1, minWidth: 0}}>
                        <div style={{fontWeight: 600, color: '#0F172A', fontSize: 14, lineHeight: 1.3}}>{page.name}</div>
                        {page.category ? (
                          <div style={{color: '#64748B', fontSize: 12, marginTop: 2}}>{page.category}</div>
                        ) : null}
                        <div style={{color: '#94A3B8', fontSize: 11, marginTop: 2}}>Page ID: {page.id}</div>
                      </div>
                      {checked && (
                        <i className="material-icons" style={{color: '#1877F2', fontSize: 18}}>check_circle</i>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button theme="light" onClick={this.closePageSelectModal} disabled={this.isFinalizingPage}>
              취소
            </Button>
            <Button
              theme="primary"
              onClick={this.confirmFacebookPageSelection}
              disabled={!this.selectedFacebookPageId || this.isFinalizingPage}
            >
              {this.isFinalizingPage ? '연동 중...' : '이 페이지로 연동'}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    );
  }
}

export default SnsSetting;
